import type {
  AdminCatalogEntry,
  AdminClientLimit,
  AdminUser,
  AdminUserPage,
  AgentDevice,
  ApiConnection,
  ApiKey,
  ApiUsage,
  CreatedApiKey,
  DeviceCapabilities,
  GeneratedImages,
  ImageRequest,
  LocalModel,
  ModelFit,
  ModelRuntimeStatus,
  ModelUpdates,
  PullProgress,
  AgentRegistration,
  AuthResponse,
  CatalogApplication,
  Conversation,
  Memory,
  Message,
  ModelEntry,
  SsoConfig,
  ToolInfo,
  TurnAccepted,
  User,
} from './types'
import { isDesktopApp } from './platform'

/** Where the desktop app talks to when nothing is configured at build time. */
const HOSTED_API = 'https://ai-api.nevtan.com'

/**
 * In the browser the Vite proxy forwards /api to the backend, so a relative
 * base is correct. The desktop shell serves from `tauri://localhost`, where a
 * relative URL resolves to the app bundle and never reaches the API — so it
 * needs an absolute one.
 *
 * <p>Which absolute address depends on the build. A packaged installer runs on
 * someone else's machine, where nothing is listening on localhost, so a release
 * build has to point at the hosted API or the app cannot sign anybody in. A
 * development build keeps pointing at the local API, which is the whole reason
 * to run the shell from source.
 *
 * <p>VITE_API_URL overrides both, which is how a staging installer is built.
 */
/**
 * Where the web app lives.
 *
 * Distinct from {@link API_BASE}: the API and the site are different hosts in
 * this deployment, and a page meant for a person -- the device approval screen
 * -- has to be opened on the one serving the app, not the one serving JSON.
 */
export const WEB_BASE: string =
  import.meta.env.VITE_WEB_URL ??
  (typeof window !== 'undefined' && window.location.protocol.startsWith('http')
    ? window.location.origin
    : 'https://ai.nevtan.com')

export const API_BASE =
  import.meta.env.VITE_API_URL ??
  (isDesktopApp ? (import.meta.env.DEV ? 'http://localhost:8080' : HOSTED_API) : '')

const TOKEN_KEY = 'nevtan.accessToken'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

/**
 * The last known signed-in user.
 *
 * Kept so a session survives the API being unreachable. Installed models run
 * on this machine, so losing the network is no reason to stop being signed in
 * -- and clearing the session would take the user's local models with it.
 */
const USER_KEY = 'nevtan.user'

export function getCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

export function setCachedUser(user: User | null) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_KEY)
  } catch {
    // A browser refusing storage is not a reason to fail a sign-in.
  }
}

/**
 * True when a failure means "could not reach the API" rather than "the API
 * said no". `fetch` rejects with a TypeError for DNS, connection and CORS
 * failures, none of which say anything about whether the session is valid.
 */
const MODELS_KEY = 'nevtan.models'

export function getCachedModels(): ModelEntry[] {
  try {
    const raw = localStorage.getItem(MODELS_KEY)
    return raw ? (JSON.parse(raw) as ModelEntry[]) : []
  } catch {
    return []
  }
}

export function setCachedModels(models: ModelEntry[]) {
  try {
    localStorage.setItem(MODELS_KEY, JSON.stringify(models))
  } catch {
    // A browser refusing storage costs offline use, not correctness.
  }
}

/** The weights filename for an on-device model, from whatever list we last saw. */
export function weightsFileFor(modelId: string): string | null {
  return getCachedModels().find((m) => m.id === modelId)?.file ?? null
}

export function isOffline(error: unknown): boolean {
  return !(error instanceof ApiError)
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Which NevTan client this is, sent with every request.
 *
 * <p>Conversations are namespaced per client on the server: this app's chats
 * and the VS Code extension's coding sessions are one account and two
 * histories. Set here rather than at each call site so nothing added later can
 * forget it.
 *
 * <p>The turn stream is the one request that cannot carry it: it goes over
 * `EventSource`, which the browser gives no way to set headers on. The server
 * reads an absent header as this client, which is why that still works.
 */
const CLIENT = 'DESKTOP'

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  // Not for a multipart body. The browser sets its own content type there,
  // with the boundary that says where each part ends -- and a boundary-less
  // `multipart/form-data` is unparseable at the other end, which fails as a
  // rejected request rather than as anything that names the cause.
  if (!(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  headers.set('X-NevTan-Client', CLIENT)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })

  if (response.status === 401 && token) {
    // A request that carried a session token got rejected — that token is
    // the thing that expired, so drop it and send the user to sign-in. A 401
    // with no token attached (a bad password, a rejected SSO exchange) is a
    // different kind of failure and keeps its own message below.
    setToken(null)
    throw new ApiError(401, 'Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new ApiError(response.status, body?.message ?? `Request failed (${response.status})`)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  return (await response.json()) as T
}

export const api = {
  // ---- auth ----
  register: (email: string, name: string, password: string) =>
    request<AuthResponse>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password }),
    }),

  login: (email: string, password: string) =>
    request<AuthResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<User>('/api/v1/auth/me'),

  // Where the SSO lives and which client id this deployment answers to — served
  // rather than baked into the build, so staging and production point at
  // different SSOs from the same bundle.
  ssoConfig: () => request<SsoConfig>('/api/v1/auth/sso/config'),

  // Trades a verified SSO access token for an ordinary session token, exactly
  // like password sign-in returns.
  /**
   * Signs in through the SSO without the browser talking to it.
   *
   * The SSO permits the website's origin but refuses `tauri://localhost`, so
   * the packaged app's preflight fails before any credential is checked. This
   * makes the call server-side, where CORS does not apply.
   */
  ssoSignin: (email: string, password: string) =>
    request<AuthResponse>('/api/v1/auth/sso/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  ssoExchange: (token: string) =>
    request<AuthResponse>('/api/v1/auth/sso/exchange', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  // ---- conversations ----
  listConversations: (query?: string) =>
    request<Conversation[]>(`/api/v1/conversations${query ? `?q=${encodeURIComponent(query)}` : ''}`),

  /**
   * Starts a conversation.
   *
   * Passing folders starts it in coding mode, because that is what the roots
   * are for: the server refuses every path until a coding conversation names
   * them, and stores them on nothing else.
   */
  createConversation: (model?: string, workspaceRoots?: string[]) =>
    request<Conversation>('/api/v1/conversations', {
      method: 'POST',
      body: JSON.stringify(
        workspaceRoots && workspaceRoots.length > 0
          ? { model, mode: 'CODING', workspaceRoots }
          : { model },
      ),
    }),

  getConversation: (id: string) => request<Conversation>(`/api/v1/conversations/${id}`),

  renameConversation: (id: string, title: string) =>
    request<Conversation>(`/api/v1/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  setConversationModel: (id: string, model: string) =>
    request<Conversation>(`/api/v1/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ model }),
    }),

  deleteConversation: (id: string) =>
    request<void>(`/api/v1/conversations/${id}`, { method: 'DELETE' }),

  listMessages: (id: string) => request<Message[]>(`/api/v1/conversations/${id}/messages`),

  sendMessage: (id: string, content: string) =>
    request<TurnAccepted>(`/api/v1/conversations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  cancelTask: (conversationId: string, taskId: string) =>
    request<void>(`/api/v1/conversations/${conversationId}/tasks/${taskId}/cancel`, {
      method: 'POST',
    }),

  // ---- approvals ----
  approve: (approvalId: string) =>
    request<unknown>(`/api/v1/approvals/${approvalId}/approve`, { method: 'POST' }),

  deny: (approvalId: string) =>
    request<unknown>(`/api/v1/approvals/${approvalId}/deny`, { method: 'POST' }),

  // ---- devices ----
  listAgents: () => request<AgentDevice[]>('/api/v1/agents'),

  /**
   * Starts pairing and returns a short code.
   *
   * The device claims the code and receives its own secret, so nothing the
   * person sees is long-lived or worth copying anywhere.
   */
  /** The device asks to be connected. Public: it has no session yet. */
  requestPairing: (device: {
    deviceName: string
    platform: string
    architecture: string
    agentVersion: string
  }) =>
    request<{ code: string; expiresAt: string }>('/api/v1/agents/pairing/request', {
      method: 'POST',
      body: JSON.stringify(device),
    }),

  /** Polls for approval. Yields the session and the device identity, once. */
  collectPairing: (code: string) =>
    request<{
      approved: boolean
      agentId: string | null
      refreshToken: string | null
      accessToken: string | null
    }>(`/api/v1/agents/pairing/${encodeURIComponent(code)}/collect`, { method: 'POST' }),

  /** What the approval page shows: which machine is asking to connect. */
  pendingPairing: (code: string) =>
    request<{
      deviceName: string
      platform: string
      architecture: string
      agentVersion: string
      approved: boolean
      expiresAt: string
    }>(`/api/v1/agents/pairing/${encodeURIComponent(code)}`),

  approvePairing: (code: string) =>
    request<{ deviceName: string; approved: boolean }>(
      `/api/v1/agents/pairing/${encodeURIComponent(code)}/approve`,
      { method: 'POST' },
    ),

  startPairing: () =>
    request<{ code: string; agentId: string; expiresAt: string }>(
      '/api/v1/agents/pairing',
      { method: 'POST' },
    ),

  registerAgent: (deviceName: string, platform: string, architecture: string) =>
    request<AgentRegistration>('/api/v1/agents/register', {
      method: 'POST',
      body: JSON.stringify({ deviceName, platform, architecture, agentVersion: '0.1.0' }),
    }),

  disconnectAgent: (id: string) =>
    request<AgentDevice>(`/api/v1/agents/${id}/disconnect`, { method: 'POST' }),

  revokeAgent: (id: string) =>
    request<AgentDevice>(`/api/v1/agents/${id}/revoke`, { method: 'POST' }),

  // ---- reference data ----
  /**
   * The model list, cached so it survives the API being unreachable.
   *
   * Offline the app still needs to know which models exist and which file each
   * on-device one uses -- without that it knows an id and cannot run it, which
   * is the difference between a model that works offline and one that does not.
   */
  listModels: async () => {
    try {
      const models = await request<ModelEntry[]>('/api/v1/models')
      setCachedModels(models)
      return models
    } catch (e) {
      const cached = getCachedModels()
      if (isOffline(e) && cached.length > 0) return cached
      throw e
    }
  },
  listTools: () => request<ToolInfo[]>('/api/v1/tools'),
  searchCatalog: (q?: string) =>
    request<CatalogApplication[]>(`/api/v1/catalog/applications${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  // ---- local models ----
  modelRuntimeStatus: () => request<ModelRuntimeStatus>('/api/v1/local-models/status'),

  listPulls: () => request<PullProgress[]>('/api/v1/local-models/pulls'),

  listLocalModels: (q?: string) =>
    request<LocalModel[]>(`/api/v1/local-models${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  pullModel: (id: string) =>
    request<{ requestId: string; modelId: string; status: string }>(
      `/api/v1/local-models/${encodeURIComponent(id)}/pull`,
      { method: 'POST' },
    ),

  removeModel: (id: string) =>
    request<{ modelId: string; removed: boolean }>(
      `/api/v1/local-models/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  listRecommendations: () => request<ModelFit[]>('/api/v1/local-models/recommendations'),

  /**
   * Makes images with an installed diffusion model.
   *
   * <p>Deliberately has no timeout of its own. The engine answers when it has
   * finished, and without a graphics card that is minutes rather than seconds
   * -- a client-side deadline here would abandon work that was going to
   * succeed, and leave the engine running it anyway.
   */
  generateImage: (id: string, options: ImageRequest) =>
    request<GeneratedImages>(
      `/api/v1/local-models/${encodeURIComponent(id)}/generate`,
      { method: 'POST', body: JSON.stringify(options) },
    ),

  /**
   * Changes a picture with an installed editing model.
   *
   * <p>Multipart rather than JSON, because the picture goes too. The body is
   * a FormData, so `request` must not set a JSON content type -- the browser
   * sets its own, with the boundary, and overriding it makes the parts
   * unreadable at the other end.
   */
  editImage: (id: string, options: ImageRequest, image: File) => {
    const form = new FormData()
    form.append('image', image)
    form.append('prompt', options.prompt)
    form.append('width', String(options.width))
    form.append('height', String(options.height))
    form.append('n', String(options.n))
    if (options.frames) form.append('frames', String(options.frames))
    return request<GeneratedImages>(
      `/api/v1/local-models/${encodeURIComponent(id)}/edit`,
      { method: 'POST', body: form },
    )
  },

  /**
   * Cached by default so opening the page never waits on the publisher's
   * servers; `refresh` forces a live check.
   */
  listModelUpdates: (refresh = false) =>
    request<ModelUpdates>(`/api/v1/local-models/updates${refresh ? '?refresh=true' : ''}`),

  updateModel: (id: string) =>
    request<{ modelId: string; installationId: string; status: string }>(
      `/api/v1/local-models/${encodeURIComponent(id)}/update`,
      { method: 'POST' },
    ),

  cancelPull: (id: string) =>
    request<{ modelId: string; cancelled: boolean }>(
      `/api/v1/local-models/${encodeURIComponent(id)}/pull`,
      { method: 'DELETE' },
    ),

  // ---- device ----
  deviceCapabilities: () => request<DeviceCapabilities>('/api/v1/device/capabilities'),

  refreshDeviceCapabilities: () =>
    request<DeviceCapabilities>('/api/v1/device/capabilities/refresh', { method: 'POST' }),

  // ---- API keys (OpenAI-compatible /v1) ----
  listApiKeys: () => request<ApiKey[]>('/api/v1/api-keys'),
  createApiKey: (name: string) =>
    request<CreatedApiKey>('/api/v1/api-keys', { method: 'POST', body: JSON.stringify({ name }) }),
  revokeApiKey: (id: string) => request<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' }),
  apiUsage: (days: number) => request<ApiUsage>(`/api/v1/api-keys/usage?days=${days}`),
  apiConnection: () => request<ApiConnection>('/api/v1/api-keys/connection'),

  // ---- memory ----
  listMemories: () => request<Memory[]>('/api/v1/memories'),
  saveMemory: (key: string, value: string) =>
    request<Memory>('/api/v1/memories', { method: 'POST', body: JSON.stringify({ key, value }) }),
  deleteMemory: (id: string) => request<void>(`/api/v1/memories/${id}`, { method: 'DELETE' }),

  // ---- administration ----
  // Every one of these answers 403 without the role. The panel is hidden from
  // people who lack it, but hiding is presentation; the server is the guard.
  adminUsers: (q: string, page: number) =>
    request<AdminUserPage>(
      `/api/v1/admin/users?page=${page}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    ),

  adminSetRole: (id: string, role: 'USER' | 'ADMIN') =>
    request<AdminUser>(`/api/v1/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  adminCatalog: (kind: 'model' | 'application') =>
    request<AdminCatalogEntry[]>(`/api/v1/admin/catalog/${kind}`),

  /** Fields left out are not changed; a negative position clears it. */
  adminUpdateEntry: (
    kind: 'model' | 'application',
    entryId: string,
    change: { hidden?: boolean; position?: number },
  ) =>
    request<AdminCatalogEntry>(`/api/v1/admin/catalog/${kind}/${encodeURIComponent(entryId)}`, {
      method: 'PUT',
      body: JSON.stringify(change),
    }),

  adminClients: () => request<AdminClientLimit[]>('/api/v1/admin/clients'),

  /** Null removes the limit, which is how a client goes back to showing all. */
  adminSetClientLimit: (client: string, maxModels: number | null) =>
    request<AdminClientLimit>(`/api/v1/admin/clients/${client}`, {
      method: 'PUT',
      body: JSON.stringify({ maxModels }),
    }),
}
